import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Users, Eye, ShoppingCart, TrendingDown, Send, RefreshCw, CheckCircle2 } from "lucide-react";

/**
 * Portal Analytics — admin view.
 *   • Per-client engagement (opens, views, checkouts, top skipped add-ons)
 *   • Per-add-on funnel (view → hover → checkout → paid) with conversion %
 *   • Bulk "resend magic link" for stale portals (0 opens or ≥N days idle)
 */
export default function PortalAnalytics() {
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [funnelTotals, setFunnelTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [daysStale, setDaysStale] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        api.get("/portal/analytics"),
        api.get("/portal/analytics/funnel"),
      ]);
      setRows(a.data.portals || []);
      setFunnel(f.data.funnel || []);
      setFunnelTotals(f.data.totals || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.opens += r.open_count || 0;
        acc.views += (r.event_counts?.view || 0);
        acc.checkouts += (r.event_counts?.checkout_started || 0);
        return acc;
      },
      { opens: 0, views: 0, checkouts: 0 }
    );
  }, [rows]);

  const skipRate = totals.views ? Math.round(((totals.views - totals.checkouts) / totals.views) * 100) : 0;

  const previewResend = async () => {
    setBusy(true);
    setDryRun(null);
    try {
      const { data } = await api.post("/portal/bulk-resend-magic-link", { days_stale: daysStale, dry_run: true });
      setDryRun(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmResend = async () => {
    if (!window.confirm(`This will email a fresh magic link to ${dryRun?.eligible || 0} client(s) whose portals haven't been opened in ${daysStale}+ days. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portal/bulk-resend-magic-link", { days_stale: daysStale, dry_run: false });
      toast.success(`Sent ${data.sent} magic link email(s). ${data.errors ? `Errors: ${data.errors}` : ""}`);
      setDryRun(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-10" data-testid="portal-analytics-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">Client engagement</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Portal Analytics</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Track magic-link portal opens, which add-ons your clients view, and which they skip.
            Use this to prioritize which up-sells to push in your next check-in call.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="portal-analytics-refresh"
          className="lf-btn-outline inline-flex items-center gap-2 text-sm">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard testId="portal-stat-opens" icon={<Users className="w-4 h-4" />} label="Portals opened" value={rows.length} sub={`${totals.opens} total opens`} />
        <StatCard testId="portal-stat-views" icon={<Eye className="w-4 h-4" />} label="Add-on views" value={totals.views} />
        <StatCard testId="portal-stat-checkouts" icon={<ShoppingCart className="w-4 h-4" />} label="Checkouts started" value={totals.checkouts} />
        <StatCard testId="portal-stat-skiprate" icon={<TrendingDown className="w-4 h-4" />} label="Skip rate" value={`${skipRate}%`} sub="viewed but not bought" />
      </div>

      {/* Funnel */}
      <div className="mb-10">
        <h2 className="font-display text-2xl font-bold mb-4">Per-add-on funnel</h2>
        <p className="text-neutral-600 text-sm mb-4">View → Hover → Checkout → Paid. Focus on high-view, low-paid rows — those are prime up-sell candidates.</p>
        {funnel.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-8 text-center text-neutral-500">
            No add-on events tracked yet. Once clients visit their portals, view counts will populate here.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 overflow-x-auto">
            <table className="lf-table w-full text-sm" data-testid="portal-funnel-table">
              <thead>
                <tr>
                  <th>Add-on</th>
                  <th className="text-right">Views</th>
                  <th className="text-right">Hovers</th>
                  <th className="text-right">Checkouts</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">View→Checkout</th>
                  <th className="text-right">Checkout→Paid</th>
                  <th className="text-right">View→Paid</th>
                </tr>
              </thead>
              <tbody>
                {funnel.map((f) => (
                  <tr key={f.addon_id} data-testid={`funnel-row-${f.addon_id}`}>
                    <td>
                      <div className="font-semibold">{f.name}</div>
                      <div className="text-[11px] text-neutral-500 font-mono">
                        {f.addon_id}{f.tier ? ` · tier ${f.tier}` : ""}{f.price ? ` · $${f.price}/mo` : ""}
                      </div>
                    </td>
                    <td className="text-right font-mono">{f.view}</td>
                    <td className="text-right font-mono">{f.hover}</td>
                    <td className="text-right font-mono">{f.checkout_started}</td>
                    <td className="text-right font-mono font-bold">{f.paid}</td>
                    <td className="text-right"><PctBadge value={f.conv_view_to_checkout} /></td>
                    <td className="text-right"><PctBadge value={f.conv_checkout_to_paid} hi /></td>
                    <td className="text-right"><PctBadge value={f.conv_view_to_paid} hi /></td>
                  </tr>
                ))}
                <tr className="bg-neutral-50 font-semibold border-t-2 border-neutral-300">
                  <td>Totals</td>
                  <td className="text-right font-mono">{funnelTotals.view || 0}</td>
                  <td className="text-right font-mono">{funnelTotals.hover || 0}</td>
                  <td className="text-right font-mono">{funnelTotals.checkout_started || 0}</td>
                  <td className="text-right font-mono">{funnelTotals.paid || 0}</td>
                  <td colSpan="3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk resend magic link */}
      <div className="mb-10 bg-white border border-neutral-200 p-6">
        <h2 className="font-display text-xl font-bold mb-2 flex items-center gap-2">
          <Send className="w-5 h-5 text-[#E24E1B]" /> Re-engage stale portals
        </h2>
        <p className="text-sm text-neutral-600 mb-4">
          Bulk-email a fresh magic link to clients who haven&apos;t opened their portal recently. Great for re-igniting quiet accounts before churn.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="overline block mb-1">Portals idle for ≥</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="365" value={daysStale}
                     onChange={(e) => { setDaysStale(parseInt(e.target.value || "0", 10)); setDryRun(null); }}
                     data-testid="days-stale-input"
                     className="lf-input w-20" />
              <span className="text-sm text-neutral-600">days</span>
            </div>
          </div>
          <button
            onClick={previewResend}
            disabled={busy}
            data-testid="portal-preview-resend"
            className="lf-btn-outline text-sm inline-flex items-center gap-2">
            {busy ? "Working…" : "Preview eligible"}
          </button>
          {dryRun && (
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="text-sm">
                <span className="font-semibold">{dryRun.eligible}</span> client{dryRun.eligible === 1 ? "" : "s"} eligible
                {dryRun.would_send_to?.length > 0 && (
                  <span className="text-neutral-500 ml-2 truncate inline-block max-w-md align-middle">
                    · {dryRun.would_send_to.slice(0, 4).join(", ")}{dryRun.would_send_to.length > 4 ? ` +${dryRun.would_send_to.length - 4} more` : ""}
                  </span>
                )}
              </div>
              {dryRun.eligible > 0 && (
                <button
                  onClick={confirmResend}
                  disabled={busy}
                  data-testid="portal-confirm-resend"
                  className="bg-[#0F2340] text-white text-sm font-semibold px-4 py-2 hover:bg-[#E24E1B] transition inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Send magic links
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Per-client detail */}
      {loading ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">
          No portal activity yet. Generate a magic link on a client detail page and share it with them to start tracking.
        </div>
      ) : (
        <>
          <h2 className="font-display text-2xl font-bold mb-4">Per-client activity</h2>
          <div className="bg-white border border-neutral-200 overflow-x-auto">
            <table className="lf-table w-full text-sm">
              <thead>
                <tr>
                  <th>Client</th>
                  <th className="text-right">Opens</th>
                  <th className="text-right">Views</th>
                  <th className="text-right">Checkouts</th>
                  <th>Top skipped add-ons</th>
                  <th>Last opened</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.client_id} data-testid={`portal-analytics-row-${r.client_id}`}>
                    <td className="font-semibold">
                      {r.business_name}<br />
                      <span className="text-xs text-neutral-500 font-normal">{r.email}</span>
                    </td>
                    <td className="text-right font-mono">{r.open_count || 0}</td>
                    <td className="text-right font-mono">{r.event_counts?.view || 0}</td>
                    <td className="text-right font-mono">{r.event_counts?.checkout_started || 0}</td>
                    <td className="text-xs">
                      {(r.addon_skipped || []).length === 0 ? (
                        <span className="text-neutral-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(r.addon_skipped || []).slice(0, 3).map((s) => (
                            <span key={s.addon_id} className="bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 font-mono">
                              {s.addon_id} · {s.views}×
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-xs text-neutral-500 font-mono">
                      {r.last_opened_at ? new Date(r.last_opened_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-neutral-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-2">
        {icon} {label}
      </div>
      <div className="font-display text-3xl font-bold text-[#0F2340]">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

function PctBadge({ value, hi = false }) {
  const v = Number(value || 0);
  const color = v === 0
    ? "text-neutral-400"
    : hi
      ? (v >= 20 ? "text-emerald-600 font-semibold" : v >= 5 ? "text-amber-600" : "text-neutral-500")
      : (v >= 30 ? "text-emerald-600" : v >= 10 ? "text-amber-600" : "text-neutral-500");
  return <span className={`font-mono ${color}`}>{v}%</span>;
}
